import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import { Check, X, Mail, Shield, User } from "lucide-react"; 

export default function Profile({ userId }) {
  const [athlete, setAthlete] = useState(null);
  const [invites, setInvites] = useState([]);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingInvite, setProcessingInvite] = useState(null); // Track specific button loading

  // 1. Fetch Profile
  useEffect(() => {
    async function fetchProfile() {
      if (!userId) return;
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", userId)
        .single();
      
      if (error) console.error("Error fetching profile:", error);
      else setAthlete(data);
    }
    fetchProfile();
  }, [userId]);

  // 2. Fetch Invites
  useEffect(() => {
    if (!athlete?.email) return;

    async function fetchInvites() {
      // NOTE: Ensure Foreign Keys exist in Supabase for this query to work:
      // coach_invites.program_id -> programs.id
      // coach_invites.team_id -> teams.id
      const { data, error } = await supabase
        .from("coach_invites")
        .select(`
          *,
          programs (name, sport),
          teams (name)
        `)
        .eq("email", athlete.email)
        .eq("status", "pending");

      if (error) console.error("Error fetching invites:", error);
      else setInvites(data || []);
    }
    fetchInvites();
  }, [athlete?.email]);

  // 3. Handle Response
  const handleInviteResponse = async (invite, action) => {
    setProcessingInvite(invite.id);
    try {
      const newStatus = action === "accept" ? "accepted" : "rejected";

      // A. Update invite status
      const { error: updateError } = await supabase
        .from("coach_invites")
        .update({ status: newStatus })
        .eq("id", invite.id);

      if (updateError) throw updateError;

      // B. If accepted, add to program_coaches
      if (action === "accept") {
        const { error: insertError } = await supabase
          .from("program_coaches")
          .insert([
            {
              program_id: invite.program_id,
              team_id: invite.team_id || null, // Handle null for program-wide
              user_id: userId,
              role: invite.role,
              status: "active",
            },
          ]);

        if (insertError) throw insertError;
        alert(`Success! You have joined ${invite.programs?.name}.`);
      }

      // C. Remove from UI
      setInvites((prev) => prev.filter((i) => i.id !== invite.id));

    } catch (err) {
      console.error("Error processing invite:", err);
      alert("Error processing invite. Please try again.");
    } finally {
      setProcessingInvite(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAthlete((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    const { error } = await supabase
      .from("users")
      .update({
        full_name: athlete.full_name,
        email: athlete.email,
        phone_number: athlete.phone_number,
        height_feet: athlete.height_feet,
        height_inches: athlete.height_inches,
        weight: athlete.weight,
        user_school: athlete.user_school,
        grade: athlete.grade,
        bio: athlete.bio,
        // ... include other fields if needed
      })
      .eq("id", userId);

    if (error) console.error("Error updating profile:", error);
    else setEditing(false);
    setLoading(false);
  };

  if (!athlete) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 pb-24 space-y-6">
      
      {/* --- HEADER --- */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col sm:flex-row items-center gap-6">
        <div className="relative">
            <img
            src={athlete.profile_img || "https://via.placeholder.com/150"}
            alt="Profile"
            className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-gray-50 shadow-md"
            />
            <div className="absolute bottom-0 right-0 bg-blue-600 p-1.5 rounded-full border-2 border-white text-white">
                <User size={16} />
            </div>
        </div>
        <div className="text-center sm:text-left flex-1">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">{athlete.full_name || "Unnamed User"}</h1>
          <p className="text-gray-500 font-medium">{athlete.role || "Athlete"}</p>
          <p className="text-gray-400 text-sm mt-1">{athlete.email}</p>
          <button
            onClick={() => setEditing(!editing)}
            className="mt-4 px-6 py-2 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-all shadow-lg shadow-gray-200 active:scale-95"
          >
            {editing ? "Cancel Edit" : "Edit Profile"}
          </button>
        </div>
      </div>

      {/* --- SECTION: COACH INVITES --- */}
      {/* This only renders if there are pending invites */}
      {invites.length > 0 && (
        <div className="animate-in slide-in-from-top-4 duration-500">
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                    <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
                        <Mail size={20} />
                    </div>
                    <h2 className="text-lg font-bold text-blue-900">Pending Invitations</h2>
                </div>
                
                <div className="grid gap-3">
                    {invites.map((invite) => (
                    <div key={invite.id} className="bg-white p-4 rounded-xl shadow-sm border border-blue-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-bold text-gray-900 text-lg">{invite.programs?.name}</h3>
                                {invite.role && (
                                    <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">
                                        {invite.role}
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                                {invite.teams?.name ? (
                                    <>Assigning to Team: <span className="font-semibold text-gray-700">{invite.teams.name}</span></>
                                ) : (
                                    <><Shield size={14}/> Program Wide Access</>
                                )}
                            </p>
                        </div>
                        
                        <div className="flex w-full sm:w-auto gap-2">
                            <button
                                onClick={() => handleInviteResponse(invite, "accept")}
                                disabled={processingInvite === invite.id}
                                className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-green-700 transition active:scale-95 disabled:opacity-50"
                            >
                                <Check size={18} /> Accept
                            </button>
                            <button
                                onClick={() => handleInviteResponse(invite, "decline")}
                                disabled={processingInvite === invite.id}
                                className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-600 px-4 py-2 rounded-lg font-bold hover:bg-gray-50 transition active:scale-95 disabled:opacity-50"
                            >
                                <X size={18} /> Decline
                            </button>
                        </div>
                    </div>
                    ))}
                </div>
            </div>
        </div>
      )}

      {/* --- FORM SECTION --- */}
      <div className="bg-white shadow-sm border border-gray-100 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4 px-1">Personal Details</h3>
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          {[
            { label: "Full Name", name: "full_name" },
            { label: "Phone Number", name: "phone_number" },
            { label: "Gender", name: "gender" },
            { label: "Grade", name: "grade" },
            { label: "Height (ft)", name: "height_feet" },
            { label: "Height (in)", name: "height_inches" },
            { label: "Weight (lbs)", name: "weight" },
            { label: "School", name: "user_school" },
            { label: "Location", name: "home_location" },
          ].map((field) => (
            <div key={field.name} className="flex flex-col group">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">{field.label}</label>
              {editing ? (
                <input
                  type="text"
                  name={field.name}
                  value={athlete[field.name] || ""}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                />
              ) : (
                <p className="text-gray-900 font-medium border-b border-gray-100 pb-2">{athlete[field.name] || "-"}</p>
              )}
            </div>
          ))}
        </div> */}

        {/* Bio */}
        <div className="mt-6">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Bio</label>
            {editing ? (
            <textarea
                name="bio"
                value={athlete.bio || ""}
                onChange={handleChange}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                rows="4"
            />
            ) : (
            <div className="bg-gray-50 p-4 rounded-xl text-gray-700 leading-relaxed text-sm">
                {athlete.bio || "No bio added yet."}
            </div>
            )}
        </div>

        {/* Save Button */}
        {editing && (
            <div className="mt-8 pt-4 border-t border-gray-100 flex justify-end">
            <button
                onClick={handleSave}
                disabled={loading}
                className="w-full sm:w-auto px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition shadow-lg shadow-blue-200 disabled:opacity-50"
            >
                {loading ? "Saving..." : "Save Changes"}
            </button>
            </div>
        )}
      </div>
    </div>
  );
}